import {Material, Object3D, Texture} from '@wonderlandengine/api';
import { vec3 } from 'gl-matrix';

/**
 * Set the diffuse/flat texture of known pipelines.
 *
 * @param mat Material to set the texture on
 * @param texture Texture to set
 * @param customTextureProperty Texture property to set or `'auto'` to automatically
 *      detect the right texture property based on known pipeline.
 * @returns `true` if the property was set, `false` otherwise.
 */
export function setFirstMaterialTexture(
    mat: Material,
    texture: Texture,
    customTextureProperty: string
) {
    if (customTextureProperty !== 'auto') {
        // @ts-ignore
        mat[customTextureProperty] = texture;
        return true;
    }

    const shader = mat.shader;
    if (shader === 'Flat Opaque Textured' ||
        shader === 'OverlayVideoOpaqueTextured') {
        // @ts-ignore
        mat.flatTexture = texture;
        return true;
    } else if (
        shader === 'Phong Opaque Textured' ||
        shader === 'Foliage' ||
        shader === 'Phong Normalmapped' ||
        shader === 'Phong Lightmapped'
    ) {
        // @ts-ignore
        mat.diffuseTexture = texture;
        return true;
    } else if (shader === 'Particle') {
        // @ts-ignore
        mat.mainTexture = texture;
        return true;
    } else if (shader === 'DistanceFieldVector') {
        // @ts-ignore
        mat.vectorTexture = texture;
        return true;
    } else if (shader === 'Background' || shader === 'Sky') {
        // @ts-ignore
        mat.texture = texture;
        return true;
    } else if (shader === 'Physical Opaque Textured') {
        // @ts-ignore
        mat.albedoTexture = texture;
        return true;
    }
    return false;
}

export function resizeToRatio(sourceWidth: number, sourceHeight: number, maxScaleWidth: number, maxScaleHeight: number, objToScale: Object3D) {
    // Validate inputs to prevent memory access errors during orientation changes
    if (!sourceWidth || !sourceHeight || sourceWidth <= 0 || sourceHeight <= 0 || 
        !isFinite(sourceWidth) || !isFinite(sourceHeight)) {
        console.warn('resizeToRatio: Invalid source dimensions', sourceWidth, sourceHeight);
        return;
    }

    const targetRatio = sourceWidth / sourceHeight;

    // Additional validation for calculated ratio
    if (!isFinite(targetRatio) || targetRatio <= 0) {
        console.warn('resizeToRatio: Invalid aspect ratio', targetRatio);
        return;
    }

    let newWidth, newHeight;

    const potentialWidthFromHeight = maxScaleHeight * targetRatio;

    if (potentialWidthFromHeight <= maxScaleWidth) {
        newHeight = maxScaleHeight;
        newWidth = newHeight * targetRatio;
    } else {
        newWidth = maxScaleWidth;
        newHeight = newWidth / targetRatio;
    }

    // Final validation before applying scale
    if (!isFinite(newWidth) || !isFinite(newHeight) || newWidth <= 0 || newHeight <= 0) {
        console.warn('resizeToRatio: Invalid calculated dimensions', newWidth, newHeight);
        return;
    }

    objToScale.setScalingLocal([newWidth, newHeight, 1]);
}

export function deg2rad(value: number) {
    return (value * Math.PI) / 180.0;
}

export function rad2deg(value: number) {
    return (value * 180.0) / Math.PI;
}

export function vectorProjection(a: vec3, b: vec3): vec3 {
    const bMagnitudeSq = Math.pow(a.length, 2);

    let returnVec: vec3 = vec3.create();

    // If the vector to project onto (B) is a zero vector, the projection is a zero vector.
    if (bMagnitudeSq === 0) {
        // Return a zero vector of the same dimension as B
        return vec3.zero(returnVec);
    }

    const scalarProjectionFactor = vec3.dot(a, b) / bMagnitudeSq;

    // Multiply the scalar factor by vector B to get the final projection vector
    vec3.scale(returnVec, b, scalarProjectionFactor);
    return returnVec;
}